/**
 * **Zähler eines Gebäudes** — Zählerhierarchie je Medium, per Drag-and-Drop wie im Datei-Manager
 * (tri-server docs/metering.md).
 *
 * - Links der Baum: Hauptzähler → Unterzähler, je Knoten Verbrauch der letzten 12 Monate und Rest
 *   (Hauptzähler minus Unterzähler).
 * - Rechts die nicht zugeordneten Zähler.
 * - Zähler auf Zähler ziehen = Unterzähler; in die rechte Liste ziehen = Zuordnung aufheben.
 *   Der Server prüft Medium und Kreisfreiheit.
 * - Ein Klick zeigt den Zähler: Mieteinheit (0..1) oder gemessenes Objekt (Etage, Raum, Anlage).
 * - Zählerstände kommen per Upload (CSV/Excel) oder live über IoT.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDrag, useDrop } from "react-dnd";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink, Gauge, Search, Upload, Wand2 } from "lucide-react";
import { useModalContext } from "../../components/ModalProvider";
import { MEDIA, Medium, MeterNode, MeterTree, RentalUnitsView, ImportReport, MeterSuggestion, MeterHint, useMeteringAPI } from "../../MeteringAPI";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DND = "METER";
/** Die Seite einer Entität im Portal (wie FloorProperties, SpaceEquipmentPanel). */
export const entityPath = (type: "Asset" | "Floor" | "Space" | "Building" | "Lease" | "LegalPerson", id: string) =>
  `/entity?selectedEntityType=${type}&selectedEntity=${encodeURIComponent(id)}`;
const NONE = "__none__";
const num = (v: number | null | undefined, unit = "") =>
  v == null ? "—" : `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(v)}${unit ? ` ${unit}` : ""}`;

interface DragItem {
  id: string;
  medium: Medium;
}

const descendants = (node: MeterNode): Set<string> => {
  const out = new Set<string>();
  const walk = (n: MeterNode): void => {
    for (const c of n.children) {
      out.add(c._id);
      walk(c);
    }
  };
  walk(node);
  return out;
};

/** Öffnet die Anlage des Zählers — in jeder Zeile, damit man nicht erst die Details suchen muss. */
function OpenAssetButton({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      aria-label={t("metering.openAsset")}
      title={t("metering.openAsset")}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
      onClick={e => {
        e.stopPropagation();
        navigate(entityPath("Asset", id));
      }}
      data-testid="meter-row-open"
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </button>
  );
}

function TreeRow({
  node,
  depth,
  unit,
  expanded,
  toggle,
  selected,
  select,
  onDropOnto,
  filter,
}: {
  node: MeterNode;
  depth: number;
  unit: string;
  expanded: Set<string>;
  toggle: (id: string) => void;
  selected: string | null;
  select: (n: MeterNode) => void;
  onDropOnto: (item: DragItem, target: MeterNode) => void;
  filter: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const below = useMemo(() => descendants(node), [node]);
  const [{ dragging }, drag] = useDrag(() => ({ type: DND, item: { id: node._id, medium: node.medium } as DragItem, collect: m => ({ dragging: m.isDragging() }) }), [node]);
  const [{ over, can }, drop] = useDrop(
    () => ({
      accept: DND,
      canDrop: (item: DragItem) => item.id !== node._id && item.medium === node.medium && !below.has(item.id) && node.upstreamMeter !== item.id,
      drop: (item: DragItem, monitor) => {
        if (monitor.didDrop()) return;
        onDropOnto(item, node);
      },
      collect: m => ({ over: m.isOver({ shallow: true }), can: m.canDrop() }),
    }),
    [node, below, onDropOnto]
  );
  const ref = useRef<HTMLDivElement>(null);
  drag(drop(ref));
  const open = expanded.has(node._id) || !!filter;
  const visibleChildren = node.children;
  return (
    <>
      <div
        ref={ref}
        role="treeitem"
        aria-expanded={node.children.length ? open : undefined}
        aria-selected={selected === node._id}
        onClick={() => select(node)}
        onDoubleClick={() => navigate(entityPath("Asset", node._id))}
        title={t("metering.openHint")}
        className={`flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 text-sm ${selected === node._id ? "bg-primary/10" : "hover:bg-muted"} ${over && can ? "ring-2 ring-primary" : ""} ${dragging ? "opacity-40" : ""}`}
        style={{ paddingLeft: `${depth * 18 + 6}px` }}
        data-testid="meter-node"
      >
        {node.children.length ? (
          <button
            type="button"
            aria-label={open ? t("metering.collapse") : t("metering.expand")}
            className="flex h-5 w-5 items-center justify-center"
            onClick={e => {
              e.stopPropagation();
              toggle(node._id);
            }}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-5" />
        )}
        <Gauge aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate" title={node.name}>
          {node.name}
        </span>
        {node.meteredUnit ? <Badge variant="secondary" className="shrink-0 text-[10px]">{t("metering.unitBadge")}</Badge> : null}
        <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{num(node.consumption, unit)}</span>
        {node.rest != null ? (
          <span className={`shrink-0 tabular-nums text-xs ${node.rest < 0 ? "text-red-600" : "text-muted-foreground"}`} title={t("metering.restHint")}>
            {t("metering.rest")} {num(node.rest)}
          </span>
        ) : null}
        <OpenAssetButton id={node._id} />
      </div>
      {open
        ? visibleChildren.map(child => (
            <TreeRow
              key={child._id}
              node={child}
              depth={depth + 1}
              unit={unit}
              expanded={expanded}
              toggle={toggle}
              selected={selected}
              select={select}
              onDropOnto={onDropOnto}
              filter={filter}
            />
          ))
        : null}
    </>
  );
}

function LooseMeter({ node, unit, selected, select }: { node: MeterNode; unit: string; selected: string | null; select: (n: MeterNode) => void }) {
  const navigate = useNavigate();
  const [{ dragging }, drag] = useDrag(() => ({ type: DND, item: { id: node._id, medium: node.medium } as DragItem, collect: m => ({ dragging: m.isDragging() }) }), [node]);
  const ref = useRef<HTMLDivElement>(null);
  drag(ref);
  return (
    <div
      ref={ref}
      onClick={() => select(node)}
      onDoubleClick={() => navigate(entityPath("Asset", node._id))}
      className={`flex cursor-grab items-center gap-1.5 rounded border px-2 py-1 text-sm ${selected === node._id ? "border-primary bg-primary/10" : "bg-background hover:bg-muted"} ${dragging ? "opacity-40" : ""}`}
      data-testid="meter-loose"
    >
      <Gauge aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate" title={node.name}>
        {node.name}
      </span>
      <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{num(node.consumption, unit)}</span>
      <OpenAssetButton id={node._id} />
    </div>
  );
}

export default function MeterHierarchyPanel({ building }: { building: any }) {
  const { t } = useTranslation();
  const api = useMeteringAPI();
  const { pushError } = useModalContext() as any;
  const buildingId = String(building?._id ?? "");
  const [data, setData] = useState<MeterTree | null>(null);
  const [view, setView] = useState<RentalUnitsView | null>(null);
  const [medium, setMedium] = useState<Medium | null>(null);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<MeterNode | null>(null);
  const [report, setReport] = useState<{ file: File; report: ImportReport } | null>(null);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<{ suggestions: MeterSuggestion[]; hints: MeterHint[]; chosen: Set<string> } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const propose = async () => {
    setBusy(true);
    try {
      const result = await api.suggestions(buildingId);
      setProposal({ ...result, chosen: new Set(result.suggestions.map(x => x.meter)) });
    } catch (error: any) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };
  const applyProposal = async () => {
    if (!proposal) return;
    setBusy(true);
    try {
      const pairs = proposal.suggestions.filter(x => proposal.chosen.has(x.meter)).map(x => ({ meter: x.meter, upstream: x.upstream }));
      const result = await api.applySuggestions(buildingId, pairs);
      setProposal(null);
      setExpanded(new Set(pairs.map(p => p.upstream)));
      await load();
      if (result.failed.length) pushError(new Error(t("metering.suggestFailed", { count: result.failed.length })));
    } catch (error: any) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    if (!buildingId) return;
    try {
      const [tree, units] = await Promise.all([api.meters(buildingId), api.rentalUnits(buildingId)]);
      setData(tree);
      setView(units);
      setMedium(current => current ?? (MEDIA.find(m => (tree.media[m] ?? []).length) || null));
    } catch (error: any) {
      pushError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  useEffect(() => {
    load();
  }, [load]);

  const roots = useMemo(() => (medium && data ? data.media[medium] ?? [] : []), [data, medium]);
  const matches = useCallback((n: MeterNode): boolean => !filter || n.name.toLowerCase().includes(filter.toLowerCase()) || (n.meterNumber ?? "").toLowerCase().includes(filter.toLowerCase()) || n.children.some(matches), [filter]);
  const linked = roots.filter(r => r.children.length > 0).filter(matches);
  const loose = roots.filter(r => r.children.length === 0).filter(matches);
  const unit = medium && data ? data.units[medium] : "";

  const move = useCallback(
    async (id: string, upstreamMeter: string | null) => {
      try {
        await api.updateMeter(id, { upstreamMeter });
        if (upstreamMeter) setExpanded(prev => new Set(prev).add(upstreamMeter));
        await load();
      } catch (error: any) {
        pushError(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load]
  );

  const [{ overLoose }, dropLoose] = useDrop(
    () => ({
      accept: DND,
      drop: (item: DragItem, monitor) => {
        // Auf einen Zähler in der Liste gezogen: der hat schon zugeordnet.
        if (!monitor.didDrop()) move(item.id, null);
      },
      collect: m => ({ overLoose: m.isOver({ shallow: true }) }),
    }),
    [move]
  );
  const looseRef = useRef<HTMLDivElement>(null);
  dropLoose(looseRef);

  // Nach jedem Laden die Auswahl aus dem frischen Baum nehmen (Attribute, nächste Eichung, Zuordnungen).
  useEffect(() => {
    if (!selected || !data) return;
    const find = (nodes: MeterNode[]): MeterNode | null => {
      for (const n of nodes) {
        if (n._id === selected._id) return n;
        const hit = find(n.children);
        if (hit) return hit;
      }
      return null;
    };
    const fresh = find(Object.values(data.media).flat() as MeterNode[]);
    if (fresh && fresh !== selected) setSelected(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const patchSelected = async (patch: any) => {
    if (!selected) return;
    try {
      await api.updateMeter(selected._id, patch);
      await load();
    } catch (error: any) {
      pushError(error);
    }
  };

  const check = async (file: File) => {
    setBusy(true);
    try {
      setReport({ file, report: await api.uploadReadings(buildingId, file, false) });
    } catch (error: any) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };
  const applyUpload = async () => {
    if (!report) return;
    setBusy(true);
    try {
      const result = await api.uploadReadings(buildingId, report.file, true);
      setReport({ file: report.file, report: result });
      await load();
    } catch (error: any) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Skeleton className="h-64 w-full" />;
  if (!data.count)
    return <p className="text-sm text-muted-foreground" data-testid="meters-empty">{t("metering.empty")}</p>;

  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="meter-hierarchy">
      <div className="flex flex-wrap items-center gap-2">
        {MEDIA.filter(m => (data.media[m] ?? []).length).map(m => (
          <Button key={m} size="sm" variant={m === medium ? "default" : "outline"} onClick={() => {
              setMedium(m);
              setSelected(null);
            }}>
            {t(`metering.medium.${m}`)}
            <span className="ml-1.5 tabular-nums opacity-70">{countAll(data.media[m] ?? [])}</span>
          </Button>
        ))}
        <div className="relative ml-auto">
          <Search aria-hidden className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder={t("metering.search")} className="h-9 w-56 pl-8" />
        </div>
        <input ref={fileInput} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={e => e.target.files?.[0] && check(e.target.files[0])} />
        <Button size="sm" variant="outline" onClick={propose} disabled={busy} data-testid="meter-propose">
          <Wand2 className="mr-1 h-4 w-4" /> {t("metering.propose")}
        </Button>
        <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()} disabled={busy}>
          <Upload className="mr-1 h-4 w-4" /> {t("metering.upload")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("metering.period", { from: new Date(data.period.from).toLocaleDateString("de-DE"), to: new Date(new Date(data.period.to).getTime() - 1).toLocaleDateString("de-DE") })}{" "}
        {t("metering.dragHint")}
      </p>

      <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0 rounded-md border p-2" role="tree" aria-label={t("metering.tree")}>
          <div className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("metering.tree")}</div>
          {linked.length ? (
            linked.map(root => (
              <TreeRow
                key={root._id}
                node={root}
                depth={0}
                unit={unit}
                expanded={expanded}
                toggle={id => setExpanded(prev => (prev.has(id) ? new Set(Array.from(prev).filter(x => x !== id)) : new Set(prev).add(id)))}
                selected={selected?._id ?? null}
                select={setSelected}
                onDropOnto={(item, target) => move(item.id, target._id)}
                filter={filter}
              />
            ))
          ) : (
            <p className="p-2 text-sm text-muted-foreground">{t("metering.treeEmpty")}</p>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          {selected ? <MeterDetails meter={selected} unit={unit} view={view} building={buildingId} onPatch={patchSelected} /> : null}
          <div ref={looseRef} className={`min-w-0 rounded-md border p-2 ${overLoose ? "ring-2 ring-primary" : ""}`} data-testid="meter-loose-list">
            <div className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("metering.loose")} <span className="tabular-nums">({loose.length})</span>
            </div>
            <div className="flex max-h-[28rem] flex-col gap-1 overflow-y-auto">
              {loose.map(n => (
                <LooseTarget key={n._id} node={n} unit={unit} selected={selected?._id ?? null} select={setSelected} onDropOnto={(item, target) => move(item.id, target._id)} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!proposal} onOpenChange={open => !open && setProposal(null)}>
        <DialogContent className="max-h-[85vh] w-[min(900px,95vw)] max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("metering.proposeTitle")}</DialogTitle>
          </DialogHeader>
          {proposal ? (
            <div className="flex flex-col gap-3 text-sm">
              <p className="text-muted-foreground">{t("metering.proposeIntro")}</p>
              {proposal.suggestions.length ? (
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-2 font-semibold">
                    <Checkbox
                      checked={proposal.chosen.size === proposal.suggestions.length}
                      onCheckedChange={(v: boolean | "indeterminate") => setProposal({ ...proposal, chosen: v === true ? new Set(proposal.suggestions.map(x => x.meter)) : new Set() })}
                    />
                    {t("metering.proposeCount", { count: proposal.suggestions.length })}
                  </label>
                  {proposal.suggestions.map(x => (
                    <label key={x.meter} className="flex min-w-0 items-start gap-2 rounded px-1 py-0.5 hover:bg-muted">
                      <Checkbox
                        className="mt-0.5"
                        checked={proposal.chosen.has(x.meter)}
                        onCheckedChange={(v: boolean | "indeterminate") => {
                          const chosen = new Set(proposal.chosen);
                          if (v === true) chosen.add(x.meter);
                          else chosen.delete(x.meter);
                          setProposal({ ...proposal, chosen });
                        }}
                      />
                      <span className="min-w-0">
                        <span className="break-words">
                          {x.meterName} <span className="text-muted-foreground">→</span> <span className="font-medium">{x.upstreamName}</span>
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {t(`metering.medium.${x.medium}`)} · {x.reason}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p>{t("metering.proposeNone")}</p>
              )}
              {proposal.hints.length ? (
                <details>
                  <summary className="cursor-pointer font-semibold">{t("metering.proposeHints", { count: proposal.hints.length })}</summary>
                  <ul className="mt-1 list-disc pl-5 text-xs">
                    {proposal.hints.map((h, i) => (
                      <li key={i} className="break-words">
                        {h.text}: {h.meters.slice(0, 8).join(", ")}
                        {h.meters.length > 8 ? " …" : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            {proposal?.suggestions.length ? (
              <Button onClick={applyProposal} disabled={busy || !proposal.chosen.size}>
                {t("metering.proposeApply", { count: proposal.chosen.size })}
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setProposal(null)}>
              {t("metering.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!report} onOpenChange={open => !open && setReport(null)}>
        <DialogContent className="max-h-[85vh] w-[min(760px,95vw)] max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("metering.uploadTitle", { file: report?.file.name ?? "" })}</DialogTitle>
          </DialogHeader>
          {report?.report.error ? (
            <p className="text-sm text-red-600">{report.report.error}</p>
          ) : report ? (
            <div className="flex flex-col gap-2 text-sm">
              <p>
                {report.report.applied
                  ? t("metering.uploadDone", { written: report.report.written ?? 0, accepted: report.report.accepted })
                  : t("metering.uploadChecked", { rows: report.report.rows, accepted: report.report.accepted })}
              </p>
              {report.report.issues.length ? <IssueList title={t("metering.issues")} items={report.report.issues} tone="text-red-600" /> : null}
              {report.report.warnings.length ? <IssueList title={t("metering.warnings")} items={report.report.warnings} tone="text-amber-700" /> : null}
            </div>
          ) : null}
          <DialogFooter>
            {report && !report.report.applied && !report.report.error && report.report.accepted > 0 ? (
              <Button onClick={applyUpload} disabled={busy}>
                {t("metering.uploadApply", { count: report.report.accepted })}
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setReport(null)}>
              {t("metering.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const countAll = (nodes: MeterNode[]): number => nodes.reduce((sum, n) => sum + 1 + countAll(n.children), 0);

/** Ein nicht zugeordneter Zähler ist zugleich Ziel: Wer einen anderen darauf zieht, macht ihn zum Hauptzähler. */
function LooseTarget({ node, unit, selected, select, onDropOnto }: { node: MeterNode; unit: string; selected: string | null; select: (n: MeterNode) => void; onDropOnto: (item: DragItem, target: MeterNode) => void }) {
  const [{ over, can }, drop] = useDrop(
    () => ({
      accept: DND,
      canDrop: (item: DragItem) => item.id !== node._id && item.medium === node.medium,
      drop: (item: DragItem, monitor) => {
        if (monitor.didDrop()) return;
        onDropOnto(item, node);
        return { handled: true };
      },
      collect: m => ({ over: m.isOver({ shallow: true }), can: m.canDrop() }),
    }),
    [node, onDropOnto]
  );
  const ref = useRef<HTMLDivElement>(null);
  drop(ref);
  return (
    <div ref={ref} className={over && can ? "rounded ring-2 ring-primary" : ""}>
      <LooseMeter node={node} unit={unit} selected={selected} select={select} />
    </div>
  );
}

function IssueList({ title, items, tone }: { title: string; items: Array<{ line: number; meter: string; problem: string }>; tone: string }) {
  return (
    <div>
      <div className="font-semibold">{title}</div>
      <ul className={`max-h-48 list-disc overflow-y-auto pl-5 ${tone}`}>
        {items.map((x, i) => (
          <li key={i}>
            {x.line ? `Zeile ${x.line}: ` : ""}
            {x.meter} — {x.problem}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MeterDetails({ meter, unit, view, building, onPatch }: { meter: MeterNode; unit: string; view: RentalUnitsView | null; building: string; onPatch: (patch: any) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useMeteringAPI();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Array<{ _id: string; name: string }>>([]);
  useEffect(() => {
    if (q.trim().length < 2) return setHits([]);
    const timer = setTimeout(() => api.searchAssets(building, q).then(rows => setHits(rows.filter(r => r._id !== meter._id))).catch(() => setHits([])), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, building, meter._id]);
  const type = meter.meteredObjectType ?? NONE;
  return (
    <div className="min-w-0 rounded-md border p-3 text-sm" data-testid="meter-details">
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="min-w-0 break-words font-semibold">{meter.name}</span>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate(entityPath("Asset", meter._id))} data-testid="meter-open-asset">
          <ExternalLink className="mr-1 h-4 w-4" /> {t("metering.openAsset")}
        </Button>
      </div>
      <MeterAttributes meter={meter} onPatch={onPatch} />
      <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-muted-foreground">{t("metering.number")}</dt>
        <dd className="break-all">{meter.meterNumber ?? meter.key}</dd>
        <dt className="text-muted-foreground">{t("metering.consumption")}</dt>
        <dd className="tabular-nums">{num(meter.consumption, unit)}</dd>
        <dt className="text-muted-foreground">{t("metering.lastReading")}</dt>
        <dd className="tabular-nums">{meter.lastReading ? `${num(meter.lastReading.value, unit)} · ${new Date(meter.lastReading.at).toLocaleDateString("de-DE")}` : "—"}</dd>
      </dl>
      <label className="mb-2 flex flex-col gap-1 text-xs">
        {t("metering.rentalUnit")}
        <Select value={meter.meteredUnit ?? NONE} onValueChange={v => onPatch({ meteredUnit: v === NONE ? null : v })}>
          <SelectTrigger className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t("metering.noUnit")}</SelectItem>
            {(view?.rentalUnits ?? []).map(u => (
              <SelectItem key={u._id} value={u._id}>
                {u.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        {t("metering.meteredObject")}
        <Select value={type} onValueChange={v => onPatch({ meteredObjectType: v === NONE ? null : v, meteredObject: null })}>
          <SelectTrigger className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t("metering.noObject")}</SelectItem>
            <SelectItem value="Floor">{t("metering.objectFloor")}</SelectItem>
            <SelectItem value="Space">{t("metering.objectSpace")}</SelectItem>
            <SelectItem value="Asset">{t("metering.objectAsset")}</SelectItem>
          </SelectContent>
        </Select>
      </label>
      {meter.meteredObjectType && meter.meteredObject ? (
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1 text-xs text-primary underline"
          onClick={() => navigate(entityPath(meter.meteredObjectType as any, meter.meteredObject as string))}
        >
          <ExternalLink className="h-3 w-3" /> {t("metering.openMeteredObject", { name: objectName(meter, view) })}
        </button>
      ) : null}
      {type === "Floor" || type === "Space" ? (
        <Select value={meter.meteredObject ?? NONE} onValueChange={v => onPatch({ meteredObjectType: type, meteredObject: v === NONE ? null : v })}>
          <SelectTrigger className="mt-2 h-8">
            <SelectValue placeholder={t("metering.choose")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t("metering.choose")}</SelectItem>
            {(type === "Floor" ? view?.floors ?? [] : view?.spaces ?? []).map((x: any) => (
              <SelectItem key={x._id} value={x._id}>
                {x.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      {type === "Asset" ? (
        <div className="mt-2 flex flex-col gap-1">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder={t("metering.assetSearch")} className="h-8" />
          {hits.map(h => (
            <button key={h._id} type="button" className="truncate rounded px-2 py-1 text-left text-xs hover:bg-muted" onClick={() => {
                onPatch({ meteredObjectType: "Asset", meteredObject: h._id });
                setQ("");
                setHits([]);
              }}>
              {h.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Name des gemessenen Objekts, soweit bekannt (Etagen und Räume aus der Mieteinheiten-Sicht). */
const objectName = (meter: MeterNode, view: RentalUnitsView | null): string => {
  if (meter.meteredObjectType === "Floor") return view?.floors.find(f => f._id === meter.meteredObject)?.name ?? meter.meteredObject ?? "";
  if (meter.meteredObjectType === "Space") return view?.spaces.find(x => x._id === meter.meteredObject)?.name ?? meter.meteredObject ?? "";
  return meter.meteredObjectName ?? meter.meteredObject ?? "";
};

const DEFAULT_YEARS: Partial<Record<Medium, number>> = { water: 6, heat: 6, cold: 6, gas: 5, electricity: 8 };
type AttrDef = { key: string; kind: "text" | "number" | "date" | "select" | "bool"; options?: string[]; media?: Medium[] };
const ATTRS: AttrDef[] = [
  { key: "zahlernummer", kind: "text" },
  { key: "zaehlerart", kind: "select", options: ["Hauptzähler", "Unterzähler", "Virtueller Zähler"] },
  { key: "einheit", kind: "select", options: ["kWh", "MWh", "m³", "l"] },
  { key: "meloId", kind: "text", media: ["electricity", "gas"] },
  { key: "maloId", kind: "text", media: ["electricity", "gas"] },
  { key: "wandlerfaktor", kind: "number", media: ["electricity"] },
  { key: "eichdatum", kind: "date" },
  { key: "eichfristJahre", kind: "number" },
  { key: "messstellenbetreiber", kind: "text" },
  { key: "fernauslesbar", kind: "bool" },
  { key: "ableseart", kind: "select", options: ["manuell", "Funk (wM-Bus)", "M-Bus", "LoRaWAN", "Smart-Meter-Gateway", "Impuls"] },
];
const READONLY = ["eichfrist", "Sekundäradresse", "Kommunikationsprotokoll", "AKS Nr"];

/** Die Zähler-Attribute, direkt editierbar: Text und Zahlen beim Verlassen des Felds, Auswahl sofort. */
function MeterAttributes({ meter, onPatch }: { meter: MeterNode; onPatch: (patch: any) => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, any>>({});
  useEffect(() => setDraft({}), [meter._id]);
  const value = (key: string) => (key in draft ? draft[key] : meter.attributes?.[key] ?? "");
  const save = (key: string, v: unknown) => {
    const before = meter.attributes?.[key] ?? "";
    if (String(v ?? "") === String(before)) return;
    onPatch({ attributes: { [key]: v === "" ? null : v } });
  };
  const next = meter.nextCalibration;
  const overdue = next && new Date(next.at).getTime() < Date.now();
  return (
    <div className="mb-3 rounded border bg-muted/30 p-2" data-testid="meter-attributes">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("metering.attributes")}</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {ATTRS.filter(a => !a.media || a.media.includes(meter.medium)).map(a => (
          <label key={a.key} className="flex min-w-0 flex-col gap-0.5 text-xs">
            <span className="text-muted-foreground">{t(`metering.attr.${a.key}`)}</span>
            {a.kind === "select" ? (
              <Select value={value(a.key) || NONE} onValueChange={v => save(a.key, v === NONE ? "" : v)}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>—</SelectItem>
                  {a.options!.map(o => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : a.kind === "bool" ? (
              <span className="flex h-8 items-center gap-2">
                <Checkbox checked={value(a.key) === "ja" || value(a.key) === true} onCheckedChange={(v: boolean | "indeterminate") => save(a.key, v === true ? "ja" : "nein")} />
                <span>{value(a.key) === "ja" || value(a.key) === true ? t("metering.yes") : t("metering.no")}</span>
              </span>
            ) : (
              <Input
                className="h-8"
                type={a.kind === "date" ? "date" : a.kind === "number" ? "number" : "text"}
                value={a.kind === "date" ? String(value(a.key)).slice(0, 10) : value(a.key)}
                placeholder={a.key === "eichfristJahre" && DEFAULT_YEARS[meter.medium] ? t("metering.defaultYears", { years: DEFAULT_YEARS[meter.medium] }) : undefined}
                onChange={e => setDraft({ ...draft, [a.key]: e.target.value })}
                onBlur={e => save(a.key, e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
              />
            )}
          </label>
        ))}
      </div>
      <div className={`mt-2 text-xs ${overdue ? "font-semibold text-red-600" : ""}`} data-testid="meter-next-calibration">
        {t("metering.nextCalibration")}:{" "}
        {next
          ? `${new Date(next.at).toLocaleDateString("de-DE")}${next.source === "computed" ? ` (${t("metering.calibrationRule", { years: next.years })})` : ""}${overdue ? ` — ${t("metering.overdue")}` : ""}`
          : t("metering.calibrationUnknown")}
      </div>
      {READONLY.some(k => meter.attributes?.[k] != null) ? (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {READONLY.filter(k => meter.attributes?.[k] != null).map(k => (
            <Fragment key={k}>
              <dt>{t(`metering.attr.${k}`, { defaultValue: k })}</dt>
              <dd className="break-all">{String(meter.attributes[k])}</dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
    </div>
  );
}
